"use client";
import React, { useEffect, useState, useRef, Suspense } from "react";
import Image from "next/image";

import Visitwebsite from "@packages/shared-components/common-utilities/cards/interaction-button/visitwebsite";
import CallNowButton from "@packages/shared-components/common-utilities/cards/interaction-button/CallNowButton";
import {
  formatRating,
  getOrdinalSuffix,
} from "@packages/shared-components/institution-profile/lib/universityProfile";
import GalleryModal from "@packages/shared-components/institution-profile/gallerymodal";
import RequestInfo from "@packages/shared-components/common-utilities/cards/interaction-button/requestinfo";
import BookOpenDay from "@packages/shared-components/common-utilities/cards/interaction-button/bookopenday";
import Getprospectus from "@packages/shared-components/common-utilities/cards/interaction-button/getprospectus";
import UserFavourite from "@packages/shared-components/common-utilities/user-favourite/UserFavourite";
import RecommendedUnisButton from "@packages/shared-components/common-utilities/cards/interaction-button/recommended-unis-button";
import {
  DataLayerGA4AttrType,
  GA4EventAttrType,
} from "@packages/lib/types/datalayerGA";
import {
  GA4DataLayer,
  GA4DataLayerFn,
} from "@packages/lib/utlils/helper-function";
import ApplyNow from "../cards/interaction-button/applynow";
import { isPGS, isWhatuni } from "@packages/lib/utlils/commonFunction";
import { ClickstreamInterface } from "@packages/lib/utlils/clickstream";
import {
  ClickstreamConstants,
  GAConstants,
} from "@packages/shared-components/services/constants";
import {
  generateProviderResultUrl,
  generateStudentReviewsUrl,
} from "@packages/lib/utlils/url-builder";
import useIsMobile from "@packages/hooks/DetectMobileDevice";
import { usePathname } from "next/navigation";
import { getCustomDomain } from "@packages/lib/utlils/common-function-server";

const ReviewThumbGallerySliderComponents = React.lazy(
  () =>
    import(
      "@packages/shared-components/common-utilities/slider/reviewthumbgalleryslidercomponents"
    ),
);
const ReviewthumbgallerysliderSkeleton = React.lazy(
  () =>
    import(
      "@packages/shared-components/skeleton/reviewthumbgalleryslider-skeleton"
    ),
);

interface UniInfo {
  institutionId: string;
  institutionName: string;
  institutionTextKey: string;
  reviewCount: number;
  overallRating: number;
  overallRatingExact: number;
  institutioLogoUrl?: string;
  wuscaRanking: number;
  latitude: string;
  longitude: string;
  isHeroBanner: boolean;
  isGallery?: boolean;
  heroImage?: string;
  scholarshipName?: string;
  isPrPage?: boolean;
  virtualTourFlag?: string;
}

interface EnquiryDetails {
  orderItemId: number;
  subOrderItemId: number;
  keyProfileId: number;
  websiteFlag: string;
  callNowFlag: string;
  website: string;
  hotLineNo: string;
  opendayFlag: string;
  prospectusFlag: string;
  emailFlag: string;
  applyNowFlag: string;
}

interface HeaderBannerProps {
  uniInfo: UniInfo;
  enquiryDetails?: EnquiryDetails;
  isClearing?: boolean;
  isVirtualTourAvailable?: boolean;
  gallery?: any;
  h1TitleFormat?: string;
  SubjectProfileDisplayname?: any;
  hasRecommendedUni?: boolean;
  commonGAAttrs?: any;
  csCommonData?: any;
  btncount?: any;
  isPostgraduate?: boolean;
  defaultFragmentId?: string;
  commonOldGAAttrs?: any;
  isIpPage?: boolean;
  hasUgCourses?: boolean;
}

const HeaderBanner: React.FC<HeaderBannerProps> = ({
  uniInfo: {
    institutionName,
    institutionId,
    reviewCount,
    overallRatingExact,
    wuscaRanking,
    institutioLogoUrl,
    isHeroBanner,
    isGallery,
    heroImage,
    institutionTextKey,
    scholarshipName,
    virtualTourFlag,
    isPrPage = false,
  },
  SubjectProfileDisplayname,
  enquiryDetails: {
    websiteFlag,
    callNowFlag,
    orderItemId,
    subOrderItemId,
    hotLineNo,
    website,
    opendayFlag,
    prospectusFlag,
    emailFlag,
    applyNowFlag,
  } = {} as any,
  isClearing,
  isVirtualTourAvailable,
  gallery,
  h1TitleFormat,
  hasRecommendedUni = false,
  commonGAAttrs,
  csCommonData,
  btncount,
  isPostgraduate,
  defaultFragmentId,
  commonOldGAAttrs,
  isIpPage = false,
  hasUgCourses,
}) => {
  const [btnHandler, setBtnHandler] = useState(false);
  const [isMobile] = useIsMobile();
  const [scrolled, setScrolled] = useState(false);
  const [height, setHeight] = useState(0);
  const [mobbtnheight, setMobBtnHeight] = useState(0);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [veppleProviders, setVeppleProviders] = useState<any>(null);
  const bannerHeader = useRef<HTMLDivElement>(null);
  const btnHeight = useRef<any>(null);
  const pathname = usePathname();
  const isScholarshipPage = pathname?.includes("scholarships") || false;
  const virtualTourUrl = `/university-profile/${institutionTextKey}/${institutionId}/#vepple`;

  // Get a value on scroll
  const handleScroll = () => {
    const width = window.innerWidth;
    const y = window.scrollY;
    const btnlength = btnHeight?.current?.offsetHeight + 20 || 0; //20px for padding buffer spacing
    const targetEl = document.body;

    if (width <= 767) {
      // Mobile
      setScrolled(y > mobbtnheight);
    } else if (width > 767 && width < 1200) {
      // Tablet
      setScrolled(y > height);
      // setIsMobile(false);
      if (y > 500) {
        targetEl.style.marginBottom = `${btnlength}px`;
      } else {
        targetEl.style.marginBottom = "0px";
      }
    } else {
      // Desktop
      setScrolled(y > height);
    }
  };

  useEffect(() => {
    // Initial check on mount
    handleScroll();

    window.addEventListener("scroll", handleScroll);

    // Cleanup event listeners on component unmount
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    const targetEl = document.body;
    const btnlength = btnHeight.current?.offsetHeight || 0;
    if (isMobile && scrolled) {
      targetEl.style.marginBottom = `${btnlength}px`;
    } else {
      targetEl.style.marginBottom = `0px`;
    }
    return () => {
      targetEl.style.marginBottom = "0px";
    };
  }, [isMobile, scrolled, btnHandler, btnHeight]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const width = window.innerWidth;

    const updateHeight = () => {
      if (width < 767) {
        if (btnHeight.current) {
          // setMobBtnPosition(btnHeight.current.offsetHeight);
          setMobBtnHeight(btnHeight.current.offsetHeight + 150);
        }
      } else {
        if (bannerHeader.current) {
          setHeight(bannerHeader.current.offsetHeight + 121);
        }
      }
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  useEffect(() => {
    const getVeppleProviders = () => {
      if (typeof window !== "undefined") {
        try {
          const stored = sessionStorage.getItem("veppleProviders");
          setVeppleProviders(stored ? JSON.parse(stored) : null);
        } catch {
          setVeppleProviders(null);
        }
      }
    };

    getVeppleProviders();

    const handleStorageChange = () => {
      getVeppleProviders();
    };

    window.addEventListener("storage", handleStorageChange);

    // Custom event for sessionStorage changes within same tab
    window.addEventListener("veppleProvidersUpdated", handleStorageChange);
  }, []);

  const finalBannerUrl = heroImage
    ? `${process.env.NEXT_PUBLIC_IMAGE_DOMAIN}${heroImage}`
    : "/static/assets/images/place-holder-images/ip-header.jpg";

  const bannerWidth =
    isHeroBanner || !heroImage ? "" : "max-w-[1216px] mx-auto";

  const finalogoUrl = institutioLogoUrl
    ? `${process.env.NEXT_PUBLIC_IMAGE_DOMAIN}${institutioLogoUrl}`
    : "";

  const openGalleryModal = () => {
    setShowModal(true);
    document.body.style.overflowY = "hidden";
  };

  const onCloseGalleryModal = () => {
    setShowModal(false);
    document.body.style.overflowY = "";
  };
  const headerGAAttrs = {
    page_section: scrolled ? "Sticky header" : "Ip header",
    ...commonGAAttrs,
  };
  const mergedOldGAAttrs = {
    data_label: isIpPage ? (scrolled ? "Sticky header" : "Ip header") : "na",
    ...commonOldGAAttrs,
  };
  const CSData = (): ClickstreamInterface => {
    return {
      ...csCommonData,
      actionType: ClickstreamConstants.clickAction,
    };
  };
  const handleVeppleClick = (virtualTourUrl: any) => {
    sessionStorage?.setItem("activeSkipLink", "Y");
    GA4DataLayer(
      handleGALogs(
        "vepple_click",
        "VIRTUAL TOUR AVAILABLE",
        getCustomDomain() + virtualTourUrl,
      ),
    );
    GA4DataLayerFn(
      handleOldGALogs(
        "vepple_redirect",
        "virtual tour available",
        typeof window !== "undefined"
          ? window.location.href + "#universitylife"
          : "",
      ),
    );
    setTimeout(() => {
      sessionStorage?.removeItem("activeSkipLink");
    }, 100);
  };
  const handleGALogs = (
    eventName: string,
    ctaName: string,
    ctaUrl: string,
  ): GA4EventAttrType => {
    return {
      event: GAConstants.track_event,
      event_name: eventName,
      ...headerGAAttrs,
      click_text: ctaName || "NA",
      field: ctaUrl,
      //cta_url: ctaUrl || "NA",
    };
  };
  const handleOldGALogs = (
    eventName: string,
    ctaName: string,
    ctaUrl: string,
  ): DataLayerGA4AttrType => {
    return {
      event: "ga_events",
      eventName: eventName,
      ...mergedOldGAAttrs,
      data_label5: "NA",
      cta_name: ctaName || "NA",
      cta_url: ctaUrl || "NA",
    };
  };

  const prPageUrl = generateProviderResultUrl({
    institutionTextkey: institutionTextKey,
    isClearing,
    isPostgraduate,
    hasUgCourses,
  });

  const studentReviewsPageUrl = generateStudentReviewsUrl({
    institutionId: institutionId,
    institutionTextkey: institutionTextKey,
  });
  return (
    <>
      <section
        ref={bannerHeader}
        className={`${scrolled ? "xl:pt-[380px]" : "lg:pb-[74px] lg:bg-white"}`}
      >
        <div
          style={
            btncount > 2
              ? { marginTop: `114px` }
              : btncount > 0 && btncount <= 2
                ? { marginTop: `69px` }
                : undefined
          }
          className={`relative md:!mt-[0px] ${scrolled ? "xl:z-[5] xl:fixed xl:bg-grey300 xl:top-[0] xl:left-[0] xl:w-full" : ""}`}
        >
          {finalBannerUrl ? (
            <Image
              className={`w-full h-[330px] md:h-[380px] xl:h-[320px] object-cover object-center ${scrolled ? "xl:hidden" : ""} ${bannerWidth}`}
              priority
              fetchPriority="high"
              src={finalBannerUrl}
              width={800}
              height={320}
              quality={100}
              placeholder="blur"
              blurDataURL="/static/assets/images/place-holder-images/ip-header.jpg"
              unoptimized={true}
              alt="header-banner"
            />
          ) : (
            <div
              className={`w-full min-h-[330px] md:min-h-[380px] xl:max-h-[320px] object-cover object-cente ${scrolled ? "xl:hidden" : ""}`}
            />
          )}
          <div
            className={`absolute top-[0] w-full py-[16px] md:py-[24px] h-full bg-gradient16 lg:bg-none transition-all duration-300 ${scrolled ? "md:bg-grey300 xl:h-fit xl:py-[12px] xl:relative" : "h-full"}`}
          >
            <div className="max-w-container mx-auto h-full px-[16px] md:px-[20px] xl:px-[0]">
              <div className="flex flex-col h-full w-full justify-between">
                <div
                  className={`flex justify-end w-full xl:pr-[24px] ${scrolled ? "xl:hidden" : ""}`}
                >
                  {gallery && gallery?.length > 0 && isGallery && (
                    <button
                      onClick={openGalleryModal}
                      className="small font-semibold hover:underline flex gap-[6px] px-[10px] py-[6px] rounded-[16px] bg-grey-100 text-grey-500"
                    >
                      <Image
                        src="/static/assets/icons/ip/show-gallery.svg"
                        alt="gallery"
                        priority
                        fetchPriority="high"
                        width={20}
                        height={20}
                      />
                      Show gallery
                    </button>
                  )}
                </div>
                <div
                  className={`flex justify-between w-full ${scrolled ? "" : "md:flex-col lg:flex-row lg:p-[24px] lg:bg-white lg:rounded-[16px] lg:border lg:border-grey200 lg:shadow-custom-2 lg:absolute lg:bottom-[-74px] lg:w-[calc(100%_-_48px)] xl:w-[1216px] lg:max-w-[100%]"}`}
                >
                  <div
                    className={`flex items-start flex-col lg:flex-row gap-[16px] w-full ${scrolled ? "xl:items-center" : ""}`}
                  >
                    <span
                      tabIndex={0}
                      className={`p-[4px] bg-white shadow-custom-1 w-[64px] h-[64px] md:min-w-[100px] md:h-[100px] rounded-[12px] ${scrolled ? "lg:!w-[56px] lg:!min-w-[56px] lg:!h-[56px] lg:!rounded-[8px] " : ""}`}
                    >
                      {finalogoUrl ? (
                        <Image
                          className="h-full rounded-[4px]"
                          src={finalogoUrl}
                          alt={`${institutionName} logo`}
                          priority
                          fetchPriority="high"
                          width={100}
                          height={100}
                        />
                      ) : (
                        <div className="h-full" />
                      )}
                    </span>
                    <div
                      className={`flex flex-col gap-[4px] w-full ${scrolled ? "xl:flex-row xl:justify-between xl:items-center" : ""}`}
                    >
                      <div>
                        <h1
                          className={`h3 text-white line-clamp-3 md:line-clamp-2 ${scrolled ? "md:text-small md:font-semibold" : "lg:text-grey300"}`}
                        >
                          {h1TitleFormat
                            ? h1TitleFormat.replace(
                                "[COLLEGE NAME]",
                                institutionName,
                              )
                            : institutionName}
                        </h1>

                        {isClearing && (
                          <span
                            tabIndex={0}
                            className={`${scrolled ? "xl:flex" : ""} hidden w-fit text-nowrap select-none rounded-[4px] font-bold uppercase px-[8px] bg-green-200 text-positive-dark x-small !leading-[20px]`}
                          >
                            clearing
                          </span>
                        )}
                        {(isVirtualTourAvailable ||
                          virtualTourFlag === "Y") && (
                          <a
                            href={
                              !isScholarshipPage &&
                              !scholarshipName &&
                              !isPrPage
                                ? "#vepple"
                                : virtualTourUrl
                            }
                            tabIndex={0}
                            className={`${scrolled ? "xl:flex" : ""} hidden w-fit text-nowrap select-none rounded-[4px] font-bold uppercase px-[8px] bg-positive-light text-positive-dark x-small !leading-[20px] hover:underline`}
                            onClick={() => handleVeppleClick(virtualTourUrl)}
                          >
                            VIRTUAL TOUR AVAILABLE
                          </a>
                        )}
                      </div>
                      {scholarshipName && (
                        <p className="font-semibold text-white lg:text-grey300">
                          {scholarshipName} scholarship
                        </p>
                      )}
                      <div
                        className={`flex justify-between ${btncount > 2 ? "md:flex-col lg:flex-row" : ""}`}
                      >
                        <div
                          className={`text-white flex flex-col gap-[8px] ${scrolled ? "xl:hidden" : ""}`}
                        >
                          {SubjectProfileDisplayname && (
                            <span
                              tabIndex={0}
                              className={`para font-semibold text-white lg:text-grey300 ${scrolled && "xl:hidden"}`}
                            >
                              {SubjectProfileDisplayname}
                            </span>
                          )}
                          {/* {breadcrumbSchema?.length > 1 && (
                        <SchemaTagLayoutComponent
                          schemaType="BreadcrumbList"
                          schemaData={generateBreadcrumbSchema(
                            breadcrumbSchema,
                            domain
                          )}
                        />
                      )} */}
                          <div
                            className={`flex flex-wrap items-center gap-x-[8px] gap-y-[4px] small ${scrolled ? "xl:hidden" : "lg:text-grey300"}`}
                          >
                            {overallRatingExact !== 0 && overallRatingExact && (
                              <span className="flex items-center">
                                <Image
                                  alt="blue star icon"
                                  className="relative top-[-1px]"
                                  width="24"
                                  height="24"
                                  priority
                                  fetchPriority="high"
                                  src="/static/assets/icons/blue-star.svg"
                                />
                                {formatRating(overallRatingExact)}
                              </span>
                            )}
                            {reviewCount !== 0 && reviewCount && (
                              <a
                                target="_blank"
                                href={studentReviewsPageUrl}
                                className="underline whitespace-nowrap "
                                onClick={() => {
                                  GA4DataLayer(
                                    handleGALogs(
                                      GAConstants.review_click,
                                      "student_reviews",
                                      studentReviewsPageUrl,
                                    ),
                                  );
                                  GA4DataLayerFn(
                                    handleOldGALogs(
                                      "review_links",
                                      "",
                                      studentReviewsPageUrl,
                                    ),
                                  );
                                }}
                              >
                                {reviewCount} student reviews
                              </a>
                            )}
                            {wuscaRanking && isWhatuni() && (
                              <a
                                target="_blank"
                                href="/student-awards-winners/wusca-rankings/university-of-the-year/"
                                className="underline whitespace-nowrap "
                                onClick={() => {
                                  GA4DataLayer(
                                    handleGALogs(
                                      "redirection_link_click",
                                      "whatuni_student_ranking",
                                      getCustomDomain() +
                                        "/student-awards-winners/wusca-rankings/university-of-the-year/",
                                    ),
                                  );
                                }}
                              >
                                Whatuni student ranking: {wuscaRanking}
                                {getOrdinalSuffix(wuscaRanking)}
                              </a>
                            )}
                          </div>
                          <ul
                            className={`flex flex-wrap gap-[8px] mt-[4px] md:mt-[0] ${scrolled ? "xl:hidden" : "lg:text-grey300"}`}
                          >
                            {isClearing && (
                              <li
                                tabIndex={0}
                                className="flex text-nowrap select-none rounded-[4px] font-bold uppercase px-[8px] bg-green-200 text-positive-dark x-small !leading-[20px]"
                              >
                                clearing
                              </li>
                            )}
                            {(isVirtualTourAvailable ||
                              virtualTourFlag === "Y") && (
                              <a
                                href={
                                  !isScholarshipPage &&
                                  !scholarshipName &&
                                  !isPrPage
                                    ? "#vepple"
                                    : virtualTourUrl
                                }
                                tabIndex={0}
                                className="flex text-nowrap select-none rounded-[4px] font-bold uppercase px-[8px] bg-positive-light text-positive-dark x-small !leading-[20px] hover:underline"
                                onClick={() =>
                                  handleVeppleClick(virtualTourUrl)
                                }
                              >
                                VIRTUAL TOUR AVAILABLE
                              </a>
                            )}
                            {!isScholarshipPage &&
                              !scholarshipName &&
                              !isPrPage && (
                                <li>
                                  <a
                                    className="small flex gap-[8px] underline whitespace-nowrap lg:text-primary-400"
                                    href={prPageUrl}
                                    onClick={() => {
                                      GA4DataLayerFn(
                                        handleOldGALogs(
                                          "providerlisting_links",
                                          "",
                                          prPageUrl,
                                        ),
                                      );
                                      GA4DataLayer(
                                        handleGALogs(
                                          "redirection_link_click",
                                          "View courses",
                                          prPageUrl,
                                        ),
                                      );
                                    }}
                                  >
                                    {`View ${isClearing ? "Clearing" : ""} courses`}
                                    <Image
                                      className="mt-[3px] lg:hidden"
                                      alt="right arrow"
                                      width={10}
                                      height={8}
                                      src="/static/assets/icons/arrow-right-white.svg"
                                    />
                                    <Image
                                      className="mt-[3px] hidden lg:block"
                                      alt="right arrow"
                                      width={10}
                                      height={8}
                                      src="/static/assets/icons/arrow-right.svg"
                                    />
                                  </a>
                                </li>
                              )}
                          </ul>
                        </div>

                        {(websiteFlag === "Y" ||
                          callNowFlag === "Y" ||
                          hotLineNo ||
                          website ||
                          !subOrderItemId ||
                          !isClearing) && (
                          <div
                            className={`flex xl:pt-[0] gap-[8px] items-end
                                ${scrolled && btncount == 0 ? "!py-[0px]" : ""}
                                ${scrolled ? "md:fixed md:z-[4] xl:relative md:bottom-[0px] xl:bottom-[unset] md:left-[0px] xl:left-[unset] xl:px-[0px] xl:py-[0px] md:w-full xl:w-fit md:bg-grey300 xl:bg-transparent xl:items-center md:px-[20px] md:py-[10px]" : "md:pt-[12px] lg:min-w-[577px]"}
                                ${btncount > 2 && !scrolled ? "xl:min-w-[700px]" : ""}`}
                          >
                            <div
                              style={
                                !scrolled && btncount > 2
                                  ? { top: `-114px` }
                                  : !scrolled && btncount > 0 && btncount <= 2
                                    ? { top: `-69px` }
                                    : undefined
                              }
                              ref={btnHeight}
                              className={`md:!top-[0] bg-grey300 md:bg-transparent md:!flex items-end md:p-[0] gap-[8px] w-full grid is-empty
                                  ${btncount && "p-[16px]"}
                                  ${scrolled ? "fixed z-[5] md:relative top-[unset] bottom-[0px] md:bottom-[unset] left-[0] md:left-[unset] md:grid-cols-4 xl:w-fit" : "left-[0px] md:left-[0px] md:top-[unset] absolute md:relative md:p-[0px] md:justify-end"}
                                  ${scrolled ? (btnHandler ? "grid-cols-1" : (btncount === 3 ? "child-hidden " : "") + (btncount === 4 ? " child-hidden-2 " : "") + "grid-cols-2") : "grid-cols-2"}
                                 `}
                            >
                              {scrolled && isMobile && btncount > 2 && (
                                <div className="absolute flex justify-center top-[-27px] left-[0] w-full md:hidden">
                                  {btnHandler ? (
                                    <span
                                      onClick={() => setBtnHandler(false)}
                                      className="bg-grey300 text-white rounded-tl-[18px] rounded-tr-[18px] x-small flex px-[12px] py-[8px]"
                                    >
                                      <Image
                                        alt="close icon"
                                        width={20}
                                        className="p-[4px] mt-[-2px]"
                                        height={20}
                                        priority
                                        fetchPriority="high"
                                        src="\static\assets\icons\search-result\close-white.svg"
                                      />
                                      Close
                                    </span>
                                  ) : (
                                    <span
                                      onClick={() => setBtnHandler(true)}
                                      className="bg-grey300 text-white rounded-tl-[18px] rounded-tr-[18px] x-small px-[12px] py-[8px]"
                                    >
                                      More
                                    </span>
                                  )}
                                </div>
                              )}
                              {!subOrderItemId &&
                                hasRecommendedUni &&
                                !isScholarshipPage &&
                                !scholarshipName && (
                                  <RecommendedUnisButton
                                    btnSize={
                                      "xl:btn-large xl:text-para w-full md:!w-auto"
                                    }
                                    defaultFragmentId={defaultFragmentId}
                                  />
                                )}
                              {(callNowFlag === "Y" || hotLineNo) &&
                                !isScholarshipPage &&
                                !scholarshipName && (
                                  <CallNowButton
                                    enquiryProps={{
                                      hotlineNumber: hotLineNo,
                                      subOrderItemId: subOrderItemId,
                                      orderItemId: orderItemId,
                                      collegeId: institutionId,
                                      qualCode: "",
                                      collegeName: institutionName,
                                      collegeDisplayName: institutionName,
                                      csPageName:
                                        typeof window !== "undefined" &&
                                        localStorage.getItem("gaPageName")
                                          ? localStorage.getItem("gaPageName")
                                          : "institutionpage",
                                      sectionName: "Ip header",
                                    }}
                                    commonGAAttrs={undefined}
                                    btnSize={
                                      isClearing
                                        ? "xl:btn-large xl:text-para w-full md:!w-auto"
                                        : ""
                                    }
                                  />
                                )}
                              {isPGS() &&
                                applyNowFlag === "Y" &&
                                !isScholarshipPage &&
                                !scholarshipName && (
                                  <ApplyNow
                                    enquiryProps={{
                                      subOrderItemId: subOrderItemId,
                                      orderItemId: orderItemId,
                                      collegeId: institutionId,
                                      qualCode: "",
                                      collegeName: institutionName,
                                      collegeDisplayName: institutionName,
                                      csPageName:
                                        typeof window !== "undefined" &&
                                        localStorage.getItem("gaPageName")
                                          ? localStorage.getItem("gaPageName")
                                          : "institutionpage",
                                      sectionName: "Ip header",
                                      studyLevelId: csCommonData?.studyLevelId,
                                    }}
                                    commonGAAttrs={headerGAAttrs}
                                    commonOldGAAttrs={mergedOldGAAttrs}
                                    btnSize={
                                      "xl:btn-large xl:text-para w-full md:!w-auto"
                                    }
                                  />
                                )}
                              {isWhatuni() &&
                                prospectusFlag === "Y" &&
                                !isScholarshipPage &&
                                !scholarshipName && (
                                  <Getprospectus
                                    enquiryProps={{
                                      subOrderItemId: subOrderItemId,
                                      orderItemId: orderItemId,
                                      collegeId: institutionId,
                                      qualCode: "",
                                      collegeName: institutionTextKey,
                                      collegeDisplayName: institutionName,
                                      csPageName:
                                        typeof window !== "undefined" &&
                                        localStorage.getItem("gaPageName")
                                          ? localStorage.getItem("gaPageName")
                                          : "institutionpage",
                                      pageName: "contenthub",
                                      sectionName: "Ip header",
                                      studyLevelId: csCommonData?.studyLevelId,
                                    }}
                                    commonGAAttrs={headerGAAttrs}
                                    commonOldGAAttrs={mergedOldGAAttrs}
                                    btnSize={
                                      "xl:btn-large xl:text-para w-full md:!w-auto"
                                    }
                                  />
                                )}
                              {(websiteFlag === "Y" || website) &&
                                !isScholarshipPage &&
                                !scholarshipName && (
                                  <Visitwebsite
                                    enquiryProps={{
                                      websiteUrl: website,
                                      subOrderItemId: subOrderItemId,
                                      orderItemId: orderItemId,
                                      collegeId: institutionId,
                                      collegeDisplayName: institutionName,
                                      qualCode: "",
                                      csPageName:
                                        typeof window !== "undefined" &&
                                        localStorage.getItem("gaPageName")
                                          ? localStorage.getItem("gaPageName")
                                          : "institutionpage",
                                      sectionName: "Ip header",
                                      studyLevelId: csCommonData?.studyLevelId,
                                    }}
                                    commonGAAttrs={headerGAAttrs}
                                    btnColor={
                                      isClearing ? "btn-blue" : "btn-grey"
                                    }
                                    // btnMinW={isClearing ? "min-w-[160px]" : ""}
                                    btnSize={
                                      "xl:btn-large xl:text-para w-full md:!w-auto"
                                    }
                                    clearing={isClearing ? true : false}
                                    commonOldGAAttrs={mergedOldGAAttrs}
                                  />
                                )}
                              {opendayFlag === "Y" &&
                                !isScholarshipPage &&
                                !scholarshipName && (
                                  <BookOpenDay
                                    enquiryProps={{
                                      subOrderItemId: subOrderItemId,
                                      orderItemId: orderItemId,
                                      collegeId: institutionId,
                                      qualCode: "",
                                      collegeName: institutionName,
                                      collegeDisplayName: institutionName,
                                      csPageName:
                                        typeof window !== "undefined" &&
                                        localStorage.getItem("gaPageName")
                                          ? localStorage.getItem("gaPageName")
                                          : "institutionpage",
                                      sectionName: "Ip header",
                                      studyLevelId: csCommonData?.studyLevelId,
                                    }}
                                    commonGAAttrs={headerGAAttrs}
                                    commonOldGAAttrs={mergedOldGAAttrs}
                                    btnSize={
                                      "xl:btn-large xl:text-para w-full md:!w-auto"
                                    }
                                  />
                                )}
                              {(emailFlag === "Y" || !subOrderItemId) &&
                                !isClearing &&
                                !isScholarshipPage &&
                                !scholarshipName && (
                                  <RequestInfo
                                    enquiryProps={{
                                      logoUrl: finalogoUrl,
                                      subOrderItemId: subOrderItemId,
                                      orderItemId: orderItemId,
                                      collegeId: institutionId,
                                      qualCode: "",
                                      collegeName: institutionTextKey,
                                      pageName: "contenthub",
                                      collegeDisplayName: institutionName,
                                      csPageName:
                                        typeof window !== "undefined" &&
                                        localStorage.getItem("gaPageName")
                                          ? localStorage.getItem("gaPageName")
                                          : "institutionpage",
                                      sectionName: "Ip header",
                                      studyLevelId: csCommonData?.studyLevelId,
                                    }}
                                    commonGAAttrs={headerGAAttrs}
                                    commonOldGAAttrs={mergedOldGAAttrs}
                                    btnSize={
                                      "xl:btn-large xl:text-para w-full md:!w-auto"
                                    }
                                  />
                                )}
                            </div>

                            {!isClearing && (
                              <UserFavourite
                                contentId={+institutionId}
                                contentName={institutionName}
                                contentType="INSTITUTION"
                                statsGAData={{
                                  page_section: headerGAAttrs?.page_section,
                                }}
                                statsOldGAData={mergedOldGAAttrs}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <GalleryModal isOpen={showModal} onClose={onCloseGalleryModal}>
        <Suspense fallback={<ReviewthumbgallerysliderSkeleton />}>
          <ReviewThumbGallerySliderComponents
            showGallery={true}
            galleryItems={gallery}
            commonGAAttrs={commonGAAttrs}
            isPostgraduate={isPostgraduate}
            isOpen={showModal}
          />
        </Suspense>
      </GalleryModal>
    </>
  );
};

export default HeaderBanner;
